/**
 * FT-265 — Pseudolocale generator for text expansion resilience testing.
 *
 * This module transforms en-US locale resource strings into the `en-XA`
 * pseudolocale format so that layout resilience issues are visible during
 * development and QA before real translations arrive.
 *
 * What the transform does:
 *   1. Replaces ASCII letters with visually distinct accented doppelgängers.
 *   2. Adds ~40 % expansion padding to simulate German / French text growth.
 *   3. Wraps the result in `[` `]` brackets so any truncation is immediately
 *      visible in the UI.
 *   4. Preserves i18next interpolation tokens `{{variable}}` verbatim so
 *      runtime substitution continues to work correctly.
 *
 * Usage:
 *   import { pseudoTransformString, pseudoTransformResource } from './pseudo';
 *
 *   // Single string
 *   pseudoTransformString('Save changes')
 *   // → '[Ŝȧvḗ ƈħȧƞɠḗŝ ẋẏẑẋẏẑ]'
 *
 *   // Entire namespace resource object (deep)
 *   pseudoTransformResource(enUsCommon)
 */

// ─────────────────────────────────────────────────────────────────────────────
// Character map
// ASCII letter → accented doppelgänger (BMP, well-supported in all browsers)
// ─────────────────────────────────────────────────────────────────────────────
const PSEUDO_CHAR_MAP: Record<string, string> = {
  a: 'ȧ', b: 'ƀ', c: 'ƈ', d: 'ḓ', e: 'ḗ', f: 'ƒ', g: 'ġ', h: 'ħ',
  i: 'ī', j: 'ĵ', k: 'ķ', l: 'ŀ', m: 'ɱ', n: 'ƞ', o: 'ơ', p: 'ƥ',
  q: 'q', r: 'ŗ', s: 'ŝ', t: 'ŧ', u: 'ū', v: 'v', w: 'ŵ', x: 'x',
  y: 'ŷ', z: 'ẑ',
  A: 'Ȧ', B: 'Ɓ', C: 'Ƈ', D: 'Ḓ', E: 'Ḗ', F: 'Ƒ', G: 'Ġ', H: 'Ħ',
  I: 'Ī', J: 'Ĵ', K: 'Ķ', L: 'Ŀ', M: 'Ṁ', N: 'Ƞ', O: 'Ơ', P: 'Ƥ',
  Q: 'Q', R: 'Ŗ', S: 'Ŝ', T: 'Ŧ', U: 'Ū', V: 'V', W: 'Ŵ', X: 'X',
  Y: 'Ŷ', Z: 'Ẑ',
};

// Repeating sequence used for expansion padding
const PADDING_SEQ = 'ẋẏẑ';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Replace a single ASCII character through the char map; non-ASCII passthrough. */
const mapChar = (c: string): string => PSEUDO_CHAR_MAP[c] ?? c;

/**
 * Build ~40 % expansion padding for a string of `sourceLength` real characters.
 * Minimum padding is 3 characters so even very short strings show expansion.
 */
const buildPadding = (sourceLength: number): string => {
  const padCount = Math.max(3, Math.ceil(sourceLength * 0.4));
  let pad = '';
  for (let i = 0; i < padCount; i++) {
    pad += PADDING_SEQ[i % PADDING_SEQ.length];
  }
  return pad;
};

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transform a single i18next string value into its `en-XA` pseudolocale form.
 *
 * Rules:
 *   - `{{variable}}` tokens are preserved verbatim so runtime interpolation works.
 *   - All other ASCII letters are replaced with accented doppelgängers.
 *   - ~40 % expansion padding is appended inside the `[` `]` wrapper.
 *
 * @example
 *   pseudoTransformString('Save')           // '[Ŝȧvḗ ẋẏẑ]'
 *   pseudoTransformString('Hello {{name}}') // '[Ħḗŀŀơ {{name}} ẋẏẑẋ]'
 */
export const pseudoTransformString = (value: string): string => {
  // Split on interpolation tokens; the capturing group keeps tokens in the array.
  // e.g. "Hello {{name}}!" → ["Hello ", "{{name}}", "!"]
  const parts = value.split(/(\{\{[^}]*\}\})/);

  let realCharCount = 0;
  const transformedParts = parts.map((part) => {
    if (part.startsWith('{{') && part.endsWith('}}')) {
      // Preserve interpolation token verbatim; count its length as ~0 for padding
      // (we don't want to pad for tokens that expand at runtime)
      return part;
    }
    realCharCount += part.length;
    return part.split('').map(mapChar).join('');
  });

  const padding = buildPadding(realCharCount);
  return `[${transformedParts.join('')} ${padding}]`;
};

/**
 * Deep-transform a locale resource object (or any nested value) to pseudolocale.
 *
 * - String leaves are passed through `pseudoTransformString`.
 * - Objects are recursed into; keys are preserved as-is (i18next uses keys
 *   internally and they should not be transformed).
 * - Non-string primitives, arrays, and null are returned unchanged.
 */
export const pseudoTransformResource = (obj: unknown): unknown => {
  if (typeof obj === 'string') {
    return pseudoTransformString(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map(pseudoTransformResource);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, val]) => [
        key,
        pseudoTransformResource(val),
      ])
    );
  }
  return obj;
};
